import { useMemo, useRef } from "react";
import { getUiLang } from "app/i18n/i18n";
import useFetchFacetValidate from "lib/hooks/useFetchFacetValidate/useFetchFacetValidate";

const langMap = {
  en: 1,
  ja: 2,
};

const keyMap = {
  analysts: "Analysts",
  gicsSectors: "Sector",
  gicsIndustryGroups: "Sector",
  topixSectors: "TOPIX Sector",
  companies: "Companies",
  reportTypes: "Report Type",
  reportTitles: "Periodicals/Report Title",
  periodicals: "Periodicals/Report Title",
  primaryPeriodicals: "Periodicals/Report Title",
  countries: "Countries/Regions",
  txCategories: "TxCategory",
  assetClasses: "TxCategory",
  txSubCategories: "TxSubCategory",
  companyWithTicker: "CompanyWithTicker",
  companySecurityType: "CompanySecurityType",
  regions: "Countries/Regions",
  currencies: "Currency",
  themes: "Theme",
  tradeIdeas: "TradeIdea",
};

// Reverse mapping from API facet keys back to tag keys
const reverseKeyMap = {
  Analysts: ["analysts"],
  Sector: ["gicsSectors", "gicsIndustryGroups"],
  "TOPIX Sector": ["topixSectors"],
  Companies: ["companies"],
  "Report Type": ["reportTypes"],
  "Periodicals/Report Title": ["reportTitles", "periodicals", "primaryPeriodicals"],
  "Countries/Regions": ["countries", "regions"],
  TxCategory: ["txCategories", "assetClasses"],
  TxSubCategory: ["txSubCategories"],
  CompanyWithTicker: ["companyWithTicker"],
  CompanySecurityType: ["companySecurityType"],
  Currency: ["currencies"],
  Theme: ["themes"],
  TradeIdea: ["tradeIdeas"],
};

export function expandSectors(tags, sectorData) {
  if (!tags?.length) return tags;

  const sectorTags = tags.filter(({ key }) => key === "gicsSectors");
  const industryGroupTags = tags.filter(({ key }) => key === "gicsIndustryGroups");
  const otherTags = tags.filter(({ key }) => key !== "gicsSectors" && key !== "gicsIndustryGroups");

  if (!sectorTags.length) return tags;

  if (!sectorData) {
    return [...otherTags, ...industryGroupTags];
  }

  const explicitIndustryGroups = new Set(industryGroupTags.map(({ tag }) => tag));
  const expandedIndustryGroups = [];

  sectorTags.forEach((sectorTag) => {
    if (!sectorTag.selected) return;

    const lang = sectorTag.lang || "en";
    const langData = sectorData[lang];
    if (!langData) return;

    const children = langData[sectorTag.tag];
    if (!children?.length) return;

    const hasExplicitChild = children.some((ig) => explicitIndustryGroups.has(ig.key));

    if (hasExplicitChild) {
      return;
    }

    children.forEach((ig) => {
      expandedIndustryGroups.push({
        key: "gicsIndustryGroups",
        tag: ig.key,
        selected: true
      });
    });
  });

  return [...otherTags, ...industryGroupTags, ...expandedIndustryGroups];
}

export function useCreateSubscriptionsRequestBody(
  selectedTags,
  selectedLanguages,
  selectedPub,
  isSubscriptionFromPillsBar,
) {
  const languageFromPreferences = selectedLanguages?.map((lang) => langMap[lang]);
  const finalLanguageFromPreferences = languageFromPreferences?.length ? languageFromPreferences : [1];
  const finalLang = isSubscriptionFromPillsBar ? finalLanguageFromPreferences : [langMap[selectedPub?.language]];

  const requestBody = selectedTags
    ?.filter(({ selected }) => selected)
    .reduce(
      (acc, { key, tag }) => {
        const finalKey = keyMap[key];
        const { facets } = acc || {};

        if (!facets[finalKey]) {
          facets[finalKey] = [];
        }

        facets[finalKey]?.push(tag);

        return acc;
      },
      { language: finalLang, facets: {} },
    );

  if (selectedPub?.id === "from-qs") {
    return requestBody;
  }

  const txCategoriesArray = selectedPub?.txCategories?.map((cat) => cat[getUiLang()]);
  const unselectedTxCategories = new Set(
    selectedTags?.filter(({ key, selected }) => key === "txCategories" && !selected).map(({ tag }) => tag),
  );
  const filteredTxCategories = txCategoriesArray?.filter((cat) => !unselectedTxCategories.has(cat));

  if (filteredTxCategories?.length) {
    const txKey = keyMap.txCategories;
    const existing = requestBody.facets[txKey] || [];
    requestBody.facets[txKey] = Array.from(new Set([...existing, ...filteredTxCategories]));
  }

  return requestBody;
}

/**
 * Filters out tags that are not found in the system based on API response.
 * @param {Array} tags - Array of tag objects with { key, tag, selected } structure
 * @param {Object} notFoundFacets - Object with facet keys and arrays of not-found values
 * @returns {Array} Filtered tags excluding not-found facets
 */
export function filterNotFoundTags(tags, notFoundFacets) {
  if (!tags?.length || !notFoundFacets || Object.keys(notFoundFacets).length === 0) {
    return tags;
  }

  // Build a set of not-found tag values for quick lookup
  const notFoundSet = new Set();

  Object.entries(notFoundFacets).forEach(([facetKey, values]) => {
    if (!Array.isArray(values)) return;

    // Get the corresponding tag keys for this facet key
    const tagKeys = reverseKeyMap[facetKey] || [];

    values.forEach((value) => {
      // Create a composite key for lookup
      tagKeys.forEach((tagKey) => {
        notFoundSet.add(`${tagKey}:${value}`);
      });
    });
  });

  return tags.filter((tag) => {
    const compositeKey = `${tag.key}:${tag.tag}`;
    return !notFoundSet.has(compositeKey);
  });
}

/**
 * Filters defaultCategoriesAndTags (for UI pills) based on not-found facets.
 * @param {Array} categoriesAndTags - Array of [category, tags[]] entries
 * @param {Object} notFoundFacets - Object with facet keys and arrays of not-found values
 * @returns {Array} Filtered categories and tags
 */
export function filterDefaultCategoriesAndTags(categoriesAndTags, notFoundFacets) {
  if (!categoriesAndTags?.length || !notFoundFacets || Object.keys(notFoundFacets).length === 0) {
    return categoriesAndTags;
  }

  // Build a set of not-found tag values for quick lookup
  const notFoundSet = new Set();

  Object.entries(notFoundFacets).forEach(([facetKey, values]) => {
    if (!Array.isArray(values)) return;

    const tagKeys = reverseKeyMap[facetKey] || [];

    values.forEach((value) => {
      tagKeys.forEach((tagKey) => {
        notFoundSet.add(`${tagKey}:${value}`);
      });
    });
  });

  return categoriesAndTags
    .map(([category, tags]) => {
      if (!Array.isArray(tags)) return [category, tags];

      const filteredTags = tags.filter((tagObj) => {
        const compositeKey = `${tagObj.key}:${tagObj.tag}`;
        return !notFoundSet.has(compositeKey);
      });

      return [category, filteredTags];
    })
    .filter(([, tags]) => Array.isArray(tags) && tags.length > 0);
}

/**
 * Flattens defaultCategoriesAndTags into a single array of tags.
 * @param {Array} categoriesAndTags - Array of [category, tags[]] entries
 * @returns {Array} Flattened array of all tags
 */
function flattenCategoriesAndTags(categoriesAndTags) {
  if (!categoriesAndTags?.length) return [];

  return categoriesAndTags.reduce((acc, [, tags]) => {
    if (Array.isArray(tags)) {
      acc.push(...tags);
    }
    return acc;
  }, []);
}

/**
 * Hook to validate subscription tags against the system and filter out invalid ones.
 * @param {Array} selectedTags - Selected subscription tags
 * @param {Array} defaultCategoriesAndTags - All available categories and tags from publication
 * @param {Array} selectedLanguages - Selected languages
 * @param {Object} selectedPub - Selected publication
 * @param {boolean} isSubscriptionFromPillsBar - Whether subscription is from pills bar
 * @returns {Object} { validatedTags, validatedCategoriesAndTags, notFoundFacets, isValidating, validationError }
 */
export function useValidatedSubscriptionTags(
  selectedTags,
  defaultCategoriesAndTags,
  selectedLanguages,
  selectedPub,
  isSubscriptionFromPillsBar,
) {
  // Cache the validation results to prevent unnecessary re-renders
  const validationResultsRef = useRef({
    notFoundFacets: {},
    isValidationComplete: false,
    requestBodyKey: null,
  });

  // Check if data has loaded - if defaultCategoriesAndTags is empty, we're still loading
  const hasDataLoaded = defaultCategoriesAndTags?.length > 0;

  // Flatten all available tags and mark them as selected for validation request
  const allTagsAsSelected = useMemo(() => {
    const flattened = flattenCategoriesAndTags(defaultCategoriesAndTags);
    return flattened.map((tag) => ({ ...tag, selected: true }));
  }, [defaultCategoriesAndTags]);

  // Use the same request body builder as estimate API
  const requestBody = useCreateSubscriptionsRequestBody(
    allTagsAsSelected,
    selectedLanguages,
    selectedPub,
    isSubscriptionFromPillsBar,
  );

  // Create a stable key to detect when request body changes
  const requestBodyKey = useMemo(
    () => JSON.stringify(requestBody),
    [requestBody],
  );

  // Only validate when there are tags to validate
  const shouldValidate = allTagsAsSelected.length > 0 && Object.keys(requestBody?.facets || {}).length > 0;

  const [response, isValidating, validationError] = useFetchFacetValidate(requestBody, shouldValidate);

  // Check if validation response has actual data (not just empty array from initial state)
  const hasValidResponse = useMemo(() => {
    const responseData = response?.data || response || {};
    return responseData && 
      (responseData.found !== undefined || responseData["not-found"] !== undefined);
  }, [response]);

  // Memoize notFoundFacets to prevent creating new references on each render
  const notFoundFacets = useMemo(() => {
    // Reset cache if request body changed (new publication, etc.)
    if (validationResultsRef.current.requestBodyKey !== requestBodyKey) {
      validationResultsRef.current = {
        notFoundFacets: {},
        isValidationComplete: false,
        requestBodyKey,
      };
    }

    // If still validating, return cached results
    if (isValidating) {
      return validationResultsRef.current.notFoundFacets;
    }

    // API returns { found: {...}, "not-found": {...} }
    const responseData = response?.data || response || {};
    const newNotFoundFacets = responseData["not-found"] || {};

    // Update cache when validation completes with valid response
    if (shouldValidate && !isValidating && hasValidResponse) {
      validationResultsRef.current = {
        notFoundFacets: newNotFoundFacets,
        isValidationComplete: true,
        requestBodyKey,
      };
    }

    return newNotFoundFacets;
  }, [response, isValidating, shouldValidate, requestBodyKey, hasValidResponse]);

  // Track if validation is truly complete:
  // - Data must be loaded first
  // - If there are tags to validate, validation must have completed with valid response
  // - If no tags to validate (hasDataLoaded but empty tags), consider it complete
  const isValidationComplete = useMemo(() => {
    // Data not loaded yet - not complete
    if (!hasDataLoaded) {
      return false;
    }
    // Data loaded, but we need to validate and it's in progress or hasn't completed
    if (shouldValidate) {
      return !isValidating && hasValidResponse && validationResultsRef.current.isValidationComplete;
    }
    // Data loaded but no tags to validate - complete
    return true;
  }, [hasDataLoaded, shouldValidate, isValidating, hasValidResponse]);

  // Filter selected tags based on validation response - only after validation completes
  const validatedTags = useMemo(() => {
    if (!isValidationComplete) {
      return selectedTags;
    }
    return filterNotFoundTags(selectedTags, notFoundFacets);
  }, [selectedTags, notFoundFacets, isValidationComplete]);

  // Filter default categories and tags for UI display - only after validation completes
  const validatedCategoriesAndTags = useMemo(() => {
    if (!isValidationComplete) {
      return defaultCategoriesAndTags;
    }
    return filterDefaultCategoriesAndTags(defaultCategoriesAndTags, notFoundFacets);
  }, [defaultCategoriesAndTags, notFoundFacets, isValidationComplete]);

  // isValidating should be true when data hasn't loaded OR validation is in progress OR waiting for valid response
  const isStillValidating = !hasDataLoaded || (shouldValidate && (isValidating || !hasValidResponse));

  return {
    validatedTags,
    validatedCategoriesAndTags,
    notFoundFacets,
    isValidating: isStillValidating,
    isValidationComplete,
    validationError,
  };
}
